import React from "react"

// Stable palette; browsers get a color by their order in step 1 so the same
// browser keeps its color across all three steps.
const PALETTE = [
  "#2C8C9C",
  "#3FBF9E",
  "#9DB4C0",
  "#22C55E",
  "#F59E0B",
  "#374151",
  "#EC4899",
  "#EF4444",
  "#8B5CF6",
  "#C4C4C4",
]

// Seconds -> "1h 12m" / "45m" / "30s" (matches PostHog's "Avg time" style).
const fmtHM = (secs) => {
  if (secs == null) return null
  const s = Math.max(0, Math.round(secs))
  const h = Math.floor(s / 3600)
  const m = Math.round((s % 3600) / 60)
  if (h > 0) return `${h}h ${m}m`
  if (s >= 60) return `${Math.floor(s / 60)}m`
  return `${s}s`
}

/**
 * Pageview funnel, by browser. steps: the API's `steps` array.
 */
const PostHogFunnel = ({ steps = [], isRTL = false, t = {} }) => {
  if (!steps.length) return null
  const firstCount = steps[0]?.count || 0

  // browser -> color from step 1 order
  const colorMap = {}
  ;(steps[0].by_browser || []).forEach((b, i) => {
    colorMap[b.browser] = PALETTE[i % PALETTE.length]
  })
  const colorFor = (b) => colorMap[b] || "#C4C4C4"

  const stepTitles = [
    t.funnelStep1 || "First page view",
    t.funnelStep2 || "Second page view",
    t.funnelStep3 || "Third page view",
  ]

  return (
    <div
      className="bg-FFFFFF"
      style={{ borderRadius: 16, border: "1px solid #EFEFEF", padding: 20 }}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div style={{ textAlign: isRTL ? "right" : "left", marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#1F1F1F" }}>
          {t.funnelByBrowser || "Pageview funnel, by browser"}
        </div>
        <div style={{ fontSize: 14, color: "#8C8C80", marginTop: 4 }}>
          {t.funnelByBrowserSub ||
            "How many users completed 1 → 2 → 3 pageviews, broken down by browser."}
        </div>
      </div>

      {steps.map((s, i) => {
        const avg = fmtHM(s.avg_time_sec)
        const last = i === steps.length - 1
        return (
          <div key={i} style={{ display: "flex", gap: 14, marginBottom: last ? 0 : 20 }}>
            {/* left rail: number + connector */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  border: "1px solid #D9D9D9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  color: "#8C8C80",
                  flex: "0 0 auto",
                }}
              >
                {s.index}
              </div>
              {!last ? (
                <div style={{ flex: 1, width: 1, background: "#E5E5E5", marginTop: 4 }} />
              ) : null}
            </div>

            {/* content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                className="d-flex align-items-center justify-content-between"
                style={{ marginBottom: 8, gap: 8 }}
              >
                <div style={{ fontSize: 16, fontWeight: 600, color: "#1F1F1F" }}>
                  {stepTitles[i]}{" "}
                  <span style={{ fontWeight: 400, color: "#8C8C80" }}>Pageview</span>
                </div>
                {avg ? (
                  <div style={{ fontSize: 13, color: "#8C8C80", whiteSpace: "nowrap" }}>
                    {(t.funnelAvgTime || "Avg time")}: {avg}
                  </div>
                ) : null}
              </div>

              {/* stacked-by-browser bar; width scales to count / firstCount */}
              <div
                style={{
                  width: "100%",
                  height: 34,
                  background: "#F3F4F6",
                  borderRadius: 8,
                  overflow: "hidden",
                  display: "flex",
                }}
              >
                {s.by_browser.map((seg, j) => (
                  <div
                    key={j}
                    title={`${seg.browser}: ${seg.count.toLocaleString()}`}
                    style={{
                      width: `${(firstCount ? seg.count / firstCount : 0) * 100}%`,
                      background: colorFor(seg.browser),
                    }}
                  />
                ))}
              </div>

              <div style={{ marginTop: 8, fontSize: 13 }}>
                {s.index === 1 ? (
                  <span style={{ color: "#12B76A" }}>
                    → {s.count.toLocaleString()} {t.funnelPersons || "persons"}
                  </span>
                ) : (
                  <span>
                    <span style={{ color: "#12B76A" }}>
                      → {s.count.toLocaleString()} ({s.completed_pct}%){" "}
                      {t.funnelCompleted || "completed step"}
                    </span>
                    <span style={{ color: "#F04438", marginInlineStart: 14 }}>
                      ↘ {s.dropped.toLocaleString()} ({s.dropped_pct}%){" "}
                      {t.funnelDropped || "dropped off"}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        )
      })}

      {/* browser legend */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          marginTop: 16,
          paddingTop: 12,
          borderTop: "1px solid #F0F0F0",
        }}
      >
        {(steps[0].by_browser || []).map((b, i) => (
          <span
            key={i}
            style={{ fontSize: 12, color: "#666", display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: colorFor(b.browser),
                display: "inline-block",
              }}
            />
            {b.browser}
          </span>
        ))}
      </div>
    </div>
  )
}

export default PostHogFunnel
